import { Injectable } from '@angular/core';
import {interval, Observable, of, Subject, Subscription, tap, throwError} from 'rxjs';
import { map } from 'rxjs/operators';

import { CompraFotosService } from './compraFotos.service';
import {
  CompraFoto,
  CompraFotoEntradasInfo,
  CompraFotoPayload,
  CompraFotoProceedToPayResponse
} from '@lib/models/compra-foto.interface';
import {
  formatTime,
  getMadridDateNow,
  parseBackendDate,
  resolveNumber,
  resolvePositiveNumber
} from "@lib/utils/compra-foto-utils";

@Injectable({ providedIn: 'root' })
export class CompraFotoReservationService {
  readonly defaultKeepAliveIntervalMs = 60_000;
  readonly defaultKeepAliveInactivityToleranceMs = 120_000;
  readonly defaultTimerDisplayThresholdSeconds = 180;

  private keepAliveIntervalMs = this.defaultKeepAliveIntervalMs;
  private keepAliveInactivityToleranceMs = this.defaultKeepAliveInactivityToleranceMs;
  private timerDisplayThresholdSeconds = this.defaultTimerDisplayThresholdSeconds;
  private reservationInitialTimeoutMinutes: number | null = null;
  private reservationKeepAliveExtensionMinutes: number | null = null;
  private ticketPrice: number | null = null;
  private ticketPricePalmerin: number | null = null;
  private porcentajeEntrada: number | null = null;
  private porcentajeEntradaEnabled: boolean = false;

  private finishDate = 0;
  private startDate = 0;
  private countdownSeconds = 0;
  private lastUserActivityAt = 0;

  private idReserva?: number;
  private tokenReserva = '';
  private reservationActive = false;

  private countdownSubscription: Subscription | null = null;
  private keepAliveSubscription: Subscription | null = null;

  private showTimer = false;
  private countdownText = '00:00';
  private isExpiringSoon = false;

  private readonly reservationExpiredSubject = new Subject<string | undefined>();
  readonly reservationExpired$ = this.reservationExpiredSubject.asObservable();

  constructor(private readonly compraFotoService: CompraFotosService) {}

  applyEntradasConfig(info: CompraFotoEntradasInfo): void {
    this.keepAliveIntervalMs = resolvePositiveNumber(info.keepAliveIntervalMs, this.defaultKeepAliveIntervalMs);
    this.keepAliveInactivityToleranceMs = resolvePositiveNumber(
      info.keepAliveInactivityToleranceMs,
      this.defaultKeepAliveInactivityToleranceMs
    );
    this.timerDisplayThresholdSeconds = resolvePositiveNumber(
      info.timerDisplayThresholdSeconds,
      this.defaultTimerDisplayThresholdSeconds
    );
    this.reservationInitialTimeoutMinutes = resolveNumber(info.reservationInitialTimeoutMinutes);
    this.reservationKeepAliveExtensionMinutes = resolveNumber(info.reservationKeepAliveExtensionMinutes);
    this.ticketPrice = resolveNumber(info.ticketPrice);
    this.ticketPricePalmerin = resolveNumber(info.ticketPricePalmerin);
    this.porcentajeEntrada = resolveNumber(info.porcentajeEntrada);
    this.porcentajeEntradaEnabled = info.porcentajeEntradaEnabled;
  }

  resetEntradasConfig(): void {
    this.keepAliveIntervalMs = this.defaultKeepAliveIntervalMs;
    this.keepAliveInactivityToleranceMs = this.defaultKeepAliveInactivityToleranceMs;
    this.timerDisplayThresholdSeconds = this.defaultTimerDisplayThresholdSeconds;
    this.reservationInitialTimeoutMinutes = null;
    this.reservationKeepAliveExtensionMinutes = null;
    this.ticketPrice = null;
    this.ticketPricePalmerin = null;
    this.porcentajeEntrada = null;
    this.porcentajeEntradaEnabled = false;
  }

  startReservation(compra: CompraFoto): void {
    this.initializeCountdown(compra);

    this.idReserva = compra.id;
    this.tokenReserva = compra.token ?? '';
    this.reservationActive = Boolean(this.idReserva && this.tokenReserva);

    this.registerActivity();
    this.handleReservationUpdate(compra);
    this.startKeepAliveLoop();
  }

  handleReservationUpdate(compra: CompraFoto | null): void {
    if (!compra) {
      return;
    }

    if (compra.estado && compra.estado !== 'RESERVADA') {
      this.triggerExpiration();
      return;
    }

    if (compra.reservaExpiracion) {
      this.finishDate = parseBackendDate(compra.reservaExpiracion);
    }
  }

  triggerExpiration(message?: string, force = false): void {
    if (!this.reservationActive && !force) {
      return;
    }

    this.clearReservationState();
    this.reservationExpiredSubject.next(message);
  }

  cancelReservation(): Observable<void> {
    if (!this.idReserva || !this.tokenReserva) {
      this.clearReservationState();
      return of(undefined);
    }

    return this.compraFotoService.cancelarCompra(this.idReserva, this.tokenReserva).pipe(
      map(() => {
        this.clearReservationState();
      })
    );
  }

  cancelReservationOnExit(): void {
    if (!this.idReserva || !this.tokenReserva) {
      this.clearReservationState();
      return;
    }

    const idReserva = this.idReserva;
    const tokenReserva = this.tokenReserva;

    this.clearReservationState();
    this.compraFotoService.cancelarCompraOnPageLeave(idReserva, tokenReserva);
  }

  proceedToPay(payload: CompraFotoPayload): Observable<CompraFotoProceedToPayResponse> {
    if (!this.idReserva || !this.tokenReserva) {
      return throwError(() => new Error('No hay una reserva activa para completar el pago.'));
    }

    const payloadWithId = {
      ...payload,
      id: this.idReserva,
    };

    this.registerActivity();

    return this.compraFotoService
      .proceedToPay(payloadWithId, this.tokenReserva)
  }

  clearReservationState(): void {
    this.stopKeepAlive();
    this.stopCountdown();
    this.reservationActive = false;
    this.countdownSeconds = 0;
    this.idReserva = undefined;
    this.tokenReserva = '';
  }

  reservationTurnFalse(){
    this.reservationActive = false;
  }

  dispose(): void {
    this.clearReservationState();
    this.resetEntradasConfig();
  }

  registerActivity(): void {
    this.lastUserActivityAt = getMadridDateNow();
  }

  hasReservationData(): boolean {
    return Boolean(this.idReserva && this.tokenReserva);
  }

  get showTimerVisible(): boolean {
    return this.showTimer;
  }

  set showTimerVisible(value: boolean) {
    this.showTimer = value;
  }

  get countdownDisplay(): string {
    return this.countdownText;
  }

  get expiringSoon(): boolean {
    return this.isExpiringSoon;
  }

  get isReservationActive(): boolean {
    return this.reservationActive;
  }

  get currentCountdownSeconds(): number {
    return this.countdownSeconds;
  }

  get currentTicketPrice(): number | null {
    return this.ticketPrice;
  }

  get currentTicketPricePalmerin(): number | null {
    return this.ticketPricePalmerin;
  }

  get reservationInitialTimeout(): number | null {
    return this.reservationInitialTimeoutMinutes;
  }

  get reservationKeepAliveExtension(): number | null {
    return this.reservationKeepAliveExtensionMinutes;
  }

  private initializeCountdown(compra: CompraFoto): void {
    this.startDate = getMadridDateNow();
    if (compra.reservaExpiracion) {
      this.finishDate = parseBackendDate(compra.reservaExpiracion);
    }
    this.startCountdown();
  }

  private startCountdown(): void {
    this.stopCountdown(false);

    this.countdownSeconds = (this.finishDate - this.startDate) / 1000;
    this.countdownText = formatTime(this.countdownSeconds);

    this.countdownSubscription = interval(1000).subscribe(() => {
      this.countdownSeconds = Math.floor((this.finishDate - getMadridDateNow()) / 1000);

      if (this.countdownSeconds <= this.timerDisplayThresholdSeconds) {
        this.showTimer = true;
      }

      this.isExpiringSoon = this.countdownSeconds <= 60;
      this.countdownText = formatTime(this.countdownSeconds);

      if (this.countdownSeconds <= 0) {
        this.stopCountdown();
        this.triggerExpiration();
      }
    });
  }

  private stopCountdown(hide = true): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = null;
    }

    if (hide) {
      this.showTimer = false;
      this.countdownText = '00:00';
      this.isExpiringSoon = false;
    }
  }

  private startKeepAliveLoop(): void {
    this.stopKeepAlive();

    if (!this.idReserva || !this.tokenReserva) {
      return;
    }

    this.keepAliveSubscription = interval(this.keepAliveIntervalMs).subscribe(() => {
      if (!this.reservationActive || !this.idReserva || !this.tokenReserva) {
        return;
      }

      const inactivity = getMadridDateNow() - this.lastUserActivityAt;

      if (inactivity > this.keepAliveInactivityToleranceMs) {
        return;
      }

      this.compraFotoService.keepAlive(this.idReserva, this.tokenReserva).subscribe({
        next: (respuesta) => this.handleReservationUpdate(respuesta),
        error: () => this.triggerExpiration()
      });
    });
  }

  get porcentajeEntradaEnabledValue(): boolean {
    return this.porcentajeEntradaEnabled;
  }

  get porcentajeEntradaValue(): number | null {
    return this.porcentajeEntrada;
  }

  private stopKeepAlive(): void {
    if (this.keepAliveSubscription) {
      this.keepAliveSubscription.unsubscribe();
      this.keepAliveSubscription = null;
    }
  }
}

